import React from 'react';
import { theme } from '../../config/theme';
import { formatDolares, formatPesos } from '../../utils/formatters';
import SaldoClienteBadge from '../cauciones/SaldoClienteBadge';

const styles = {
    panel: {
        gridColumn: 'span 3',
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '1.25rem',
        padding: '0.85rem 1rem',
        backgroundColor: '#F9FAFB',
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius,
    },
    item: { display: 'flex', flexDirection: 'column', gap: '0.3rem', alignItems: 'flex-start' },
    label: {
        fontSize: '0.7rem', fontWeight: '600', color: theme.colors.textSecondary,
        textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
    },
    impacto: {
        fontSize: '0.85rem', fontWeight: '700', fontVariantNumeric: 'tabular-nums',
        padding: '0.3rem 0', color: theme.colors.secondary,
    },
    nota: {
        width: '100%', margin: 0, fontSize: '0.75rem', fontStyle: 'italic',
        color: theme.colors.textSecondary,
    },
};

// Impacto de la instrucción sobre el saldo, con signo explícito. Usa los mismos
// formateadores que el badge para que el panel se lea como una sola unidad.
const formatImpactoSaldo = (impacto, moneda) => {
    const monto = moneda === 'Pesos' ? formatPesos(Math.abs(impacto)) : formatDolares(Math.abs(impacto));
    if (impacto > 0) return `+ ${monto}`;
    if (impacto < 0) return `- ${monto}`;
    return monto;
};

/*
 * Saldo del cliente y saldo proyectado post carga dentro del formulario de
 * instrucciones: el comercial ve cómo queda la cuenta antes de guardar.
 *
 * `preview` es lo que devuelve buildSaldoPreview (utils/instruccionesSaldoPreview).
 * Con preview null no se renderiza nada: la instrucción no mueve efectivo.
 */
const SaldoImpactoPanel = ({ preview }) => {
    if (!preview) return null;

    if (preview.sinSaldoSeguido) {
        return (
            <div style={styles.panel}>
                <p style={styles.nota}>
                    Las instrucciones en {preview.moneda} no impactan ninguno de los saldos que sigue la planilla.
                </p>
            </div>
        );
    }

    const { saldoActual, saldoPostCarga, impacto, moneda, montoDesconocido, excluyeEdicion, nota } = preview;

    if (!saldoActual.tieneSnapshot) {
        return (
            <div style={styles.panel}>
                <p style={styles.nota}>
                    Todavía no hay saldos del custodio cargados para hoy: no se puede proyectar el saldo del cliente.
                </p>
            </div>
        );
    }

    return (
        <div style={styles.panel}>
            <div style={styles.item}>
                <span style={styles.label}>
                    Saldo actual{excluyeEdicion ? ' (sin esta instrucción)' : ''}
                </span>
                <SaldoClienteBadge
                    saldoReal={saldoActual.saldoReal}
                    saldoProyectado={saldoActual.saldoProyectado}
                    moneda={moneda}
                    tieneSnapshot
                />
            </div>
            <div style={styles.item}>
                <span style={styles.label}>Impacto</span>
                <span style={styles.impacto}>
                    {montoDesconocido ? 'A definir' : formatImpactoSaldo(impacto, moneda)}
                </span>
            </div>
            <div style={styles.item}>
                <span style={styles.label}>Saldo proyectado post carga</span>
                {montoDesconocido ? (
                    <span style={styles.impacto}>-</span>
                ) : (
                    <SaldoClienteBadge
                        saldoReal={saldoPostCarga}
                        saldoProyectado={saldoPostCarga}
                        moneda={moneda}
                        tieneSnapshot
                    />
                )}
            </div>
            {nota && <p style={styles.nota}>{nota}</p>}
        </div>
    );
};

export default SaldoImpactoPanel;
